import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Switch,
  Spin,
  Empty,
  Alert,
  Statistic,
  message,
} from 'antd';
import {
  BarChartOutlined,
  LinkOutlined,
  CopyOutlined,
  PlusOutlined,
  DeleteOutlined,
  DollarOutlined,
  EyeOutlined,
  SwapOutlined,
  PercentageOutlined,
  ClockCircleOutlined,
  MoneyCollectOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { affiliateService } from '../services/api';

const { Title, Paragraph, Text } = Typography;

const formatNPR = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const Affiliate = () => {
  const [dashboard, setDashboard] = useState(null);
  const [links, setLinks] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchLinks();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await affiliateService.getDashboard();
      const data = response.data?.data || response.data;
      setDashboard(data);
      if (data?.recent_earnings) {
        setEarnings(Array.isArray(data.recent_earnings) ? data.recent_earnings : []);
      }
    } catch (err) {
      console.error('Error fetching affiliate dashboard:', err);
      setError(err.response?.data?.message || 'Failed to load affiliate dashboard.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinks = async () => {
    try {
      const response = await affiliateService.getLinks();
      const data = response.data?.data || response.data || [];
      setLinks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching affiliate links:', err);
    }
  };

  const handleCreateLink = async (values) => {
    setCreating(true);
    try {
      await affiliateService.createLink(values);
      message.success('Affiliate link created!');
      setCreateModal(false);
      createForm.resetFields();
      fetchLinks();
      fetchDashboard();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to create link.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleLink = async (id) => {
    try {
      await affiliateService.toggleLink(id);
      message.success('Link status updated.');
      fetchLinks();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to toggle link.');
    }
  };

  const handleDeleteLink = async (id) => {
    Modal.confirm({
      title: 'Delete Affiliate Link',
      content: 'Are you sure you want to delete this link?',
      onOk: async () => {
        try {
          await affiliateService.deleteLink(id);
          message.success('Link deleted.');
          fetchLinks();
        } catch (error) {
          message.error(error.response?.data?.message || 'Failed to delete link.');
        }
      },
    });
  };

  const handleCopyLink = (code) => {
    const url = `${window.location.origin}/?ref=${code}`;
    navigator.clipboard?.writeText(url);
    message.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="customer-page" style={{ padding: 0, textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-page" style={{ padding: 0, paddingTop: 40 }}>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  const statsCards = [
    {
      title: 'Total Clicks',
      value: dashboard?.total_clicks || 0,
      icon: <EyeOutlined />,
      color: '#667eea',
    },
    {
      title: 'Total Conversions',
      value: dashboard?.total_conversions || 0,
      icon: <SwapOutlined />,
      color: '#52c41a',
    },
    {
      title: 'Conversion Rate',
      value: dashboard?.conversion_rate ? `${(dashboard.conversion_rate * 100).toFixed(1)}%` : '0%',
      icon: <PercentageOutlined />,
      color: '#faad14',
    },
    {
      title: 'Total Earnings',
      value: formatNPR(dashboard?.total_earnings),
      icon: <MoneyCollectOutlined />,
      color: '#52b788',
    },
    {
      title: 'Pending Earnings',
      value: formatNPR(dashboard?.pending_earnings),
      icon: <ClockCircleOutlined />,
      color: '#f59e0b',
    },
  ];

  const linkColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => name || 'Unnamed Link',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <Text code strong>
          {code}
        </Text>
      ),
      width: 120,
    },
    {
      title: 'Link',
      key: 'link',
      render: (_, record) => {
        const url = `${window.location.origin}/?ref=${record.code}`;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text ellipsis style={{ maxWidth: 200, fontSize: '0.85rem' }} copyable={{ text: url }}>
              {url}
            </Text>
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleCopyLink(record.code)}
              size="small"
            />
          </div>
        );
      },
    },
    {
      title: 'Clicks',
      dataIndex: 'clicks',
      key: 'clicks',
      render: (val) => val || 0,
      width: 80,
    },
    {
      title: 'Conversions',
      dataIndex: 'conversions',
      key: 'conversions',
      render: (val) => val || 0,
      width: 100,
    },
    {
      title: 'Commission',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      render: (rate) => (rate ? `${rate}%` : '-'),
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleLink(record.id)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<StopOutlined />}
          size="small"
        />
      ),
      width: 80,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteLink(record.id)}
          size="small"
        />
      ),
      width: 60,
    },
  ];

  const earningsColumns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => formatDate(d),
      width: 120,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'commission' ? 'green' : 'blue'}>
          {(type || '').replace(/_/g, ' ')}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (d) => d || '-',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          +{formatNPR(amount)}
        </Text>
      ),
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = { pending: 'orange', paid: 'green', cancelled: 'red' };
        return <Tag color={colors[status] || 'default'}>{(status || '').replace(/_/g, ' ')}</Tag>;
      },
      width: 100,
    },
  ];

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>
          <BarChartOutlined style={{ marginRight: 8 }} />
          Affiliate Dashboard
        </Title>
        <Paragraph>Track your referrals, earnings, and manage affiliate links.</Paragraph>
      </div>

      <Row gutter={[16, 16]}>
        {statsCards.map((stat, index) => (
          <Col xs={12} sm={12} md={6} lg={4} key={index}>
            <Card
              className="customer-card"
              hoverable
              style={{ textAlign: 'center', height: '100%' }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 24,
                  background: `linear-gradient(135deg, ${stat.color}22, ${stat.color}44)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                }}
              >
                <span style={{ fontSize: 22, color: stat.color }}>{stat.icon}</span>
              </div>
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={{ fontSize: '1.3rem', fontWeight: 700 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        className="customer-card"
        title={
          <span style={{ fontWeight: 600 }}>
            <LinkOutlined style={{ marginRight: 8 }} />
            Affiliate Links
          </span>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModal(true)}
            size="small"
          >
            Create Link
          </Button>
        }
        style={{ marginTop: 24 }}
      >
        <Table
          columns={linkColumns}
          dataSource={links}
          rowKey="id"
          pagination={false}
          scroll={{ x: 700 }}
          locale={{ emptyText: <Empty description="No affiliate links yet. Create one!" /> }}
        />
      </Card>

      <Card
        className="customer-card"
        title={
          <span style={{ fontWeight: 600 }}>
            <DollarOutlined style={{ marginRight: 8 }} />
            Recent Earnings
          </span>
        }
        extra={
          <Button
            className="customer-btn-secondary"
            icon={<SyncOutlined />}
            size="small"
            onClick={fetchDashboard}
          >
            Refresh
          </Button>
        }
        style={{ marginTop: 24 }}
      >
        <Table
          columns={earningsColumns}
          dataSource={earnings}
          rowKey="id"
          pagination={{ pageSize: 10, size: 'small' }}
          locale={{ emptyText: <Empty description="No earnings yet." /> }}
        />
      </Card>

      <Modal
        title="Create Affiliate Link"
        open={createModal}
        onCancel={() => {
          setCreateModal(false);
          createForm.resetFields();
        }}
        footer={null}
        width={480}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateLink} size="large">
          <Form.Item
            name="name"
            label="Link Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g. Social Media, Blog Post" />
          </Form.Item>

          <Form.Item name="code" label="Custom Code (optional)">
            <Input placeholder="Leave blank for auto-generated" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={creating}
              icon={<PlusOutlined />}
              block
            >
              Create Link
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Affiliate;
